import React, { useEffect, useMemo } from 'react'
import { useEditor, EditorContent, JSONContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Mention from '@tiptap/extension-mention'
import Emoji, { gitHubEmojis } from '@tiptap/extension-emoji'
import { deserializeFromMarkdown, parseMarkdownToTiptapDoc } from '@/tiptap/markdown'

function hasParseError(content: unknown): boolean {
  if (!content || typeof content !== 'object') return false

  const doc = content as {
    type?: string
    content?: Array<{ type?: string; content?: Array<{ type?: string; text?: string }> }>
  }

  if (doc.type !== 'doc' || !Array.isArray(doc.content)) return false

  return doc.content.some((node) => {
    if (!node || typeof node !== 'object') return false
    if (!Array.isArray(node.content)) return false
    return node.content.some(
      (child) => child?.type === 'text' && child.text === 'Error parsing content',
    )
  })
}

interface CommentViewerProps {
  json?: any
  text?: string
  className?: string
}

export function CommentViewer({ json, text, className }: CommentViewerProps) {
  const resolvedContent = useMemo<
    | { kind: 'json'; value: unknown }
    | { kind: 'empty'; value: '' }
  >(() => {
    if (json) return { kind: 'json', value: json }

    if (text) {
      try {
        const parsed = parseMarkdownToTiptapDoc(text)
        if (!hasParseError(parsed)) {
          return { kind: 'json', value: parsed }
        }
      } catch (error) {
        console.error('Failed to parse markdown comment content', error)
      }

      const value = deserializeFromMarkdown(text)
      return { kind: 'json', value: value }
    }

    return { kind: 'empty', value: '' }
  }, [json, text])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ hardBreak: {
        keepMarks: true
      }  }),
      Mention.configure({
        HTMLAttributes: { class: 'text-primary font-medium' },
        // no suggestion in read mode
        suggestion: { items: () => [] },
      }),
      Emoji.configure({
        emojis: gitHubEmojis.filter((e) => !e.name.includes('regional')),
        forceFallbackImages: true,
      }),
    ],
    editable: false,
    content: resolvedContent.value ?? '',
  })

  useEffect(() => {
    if (!editor) return
    try {
      if (resolvedContent.kind === 'json') {
        editor.commands.setContent(resolvedContent.value as JSONContent)
      } else {
        editor.commands.clearContent()
      }
    } catch (error) {
      console.error('Failed to update comment content', error)
    }
  }, [editor, resolvedContent])

  return (
    <div className={className}>
      <EditorContent editor={editor} className='comment-content [&_p]:!text-sm' />
    </div>
  )
}
